/**
 * Builds a self-contained reclassification prompt that can be pasted into ANY
 * chat LLM (ChatGPT, Claude, Gemini, ...) instead of calling the Asset
 * Classification Server.
 *
 * Two reply formats are supported:
 *  - JSON (default) — Copy Prompt -> paste in chat -> copy the reply -> Paste AI
 *    Result. Nothing touches the filesystem. `parseReclassificationJson` below
 *    reads the reply back.
 *  - CSV — Copy Prompt -> paste in chat -> save reply as .csv -> Upload CSV. The
 *    header matches EXACTLY what `handleImportCSV` in AssetReclassification.js
 *    already accepts.
 *
 * Both formats carry the same fields and go through the same merge step, so they
 * are interchangeable; JSON just skips the save/upload round trip.
 *
 * Data assumptions / reliability notes:
 *  - The classification rules below are a copy of the server prompts
 *    (COMBINED_CLASSIFY_PROMPT / BATCH_CLASSIFY_PROMPT in
 *    asset-classification-server/app/reclassifier.py) so both routes produce
 *    comparable output. Keep them in sync when the server prompt changes.
 *  - Every model is listed with its real `model_id`. The importer matches on
 *    that id first and only falls back to the exact model name, so a row the
 *    LLM mangles stays UNMATCHED and is reported — it is never applied to a
 *    similar-looking model.
 *  - The sub-type code is deliberately never requested. The app derives it
 *    (keeping the existing AssetPro code when the sub-type did not change),
 *    so the LLM never invents a code.
 *  - Nothing here writes to AssetPro. The operator still reviews every row in
 *    the table and picks what to apply.
 */

// Rough token cost per model, measured on realistic AssetPro rows
// (24-char id + full model name + current classification). ~4 chars/token.
const TOKENS_PER_MODEL_IN = 75;
const TOKENS_PER_MODEL_OUT = 65;
const RULES_TOKENS = 1200; // fixed prompt preamble

// Models per prompt part.
//
// The binding constraint is the REPLY length, not the context window. Input is
// a non-issue — even 2000 models is ~150k input tokens, comfortable for any
// current model. But the reply carries one JSON object per model at ~65 tokens
// each, and max output tokens have not grown the way context windows have:
// 64k for Claude Opus 5 and Gemini 3 Pro, 128k for GPT-5.x. A truncated reply
// is the real failure mode.
//
// 64k output / 65 tokens per model ≈ 985 models. 800 leaves headroom for a
// model that pretty-prints or pads its JSON. Below this, ALWAYS emit a single
// prompt — splitting hurts quality, because one call sees every model at once
// and produces consistent Category/Type wording, whereas split parts can only
// be *asked* to stay consistent.
//
// This is deliberately NOT the server's API_BATCH_THRESHOLD (150), which
// existed for gpt-4.1-mini's 32k output cap and does not apply to a chat LLM.
export const PROMPT_BATCH_SIZE = 800;

/**
 * Estimated token cost of classifying `count` models in one prompt.
 * Shown in the UI so the operator can see the reply size rather than having a
 * cap silently applied on their behalf.
 */
export function estimatePromptTokens(count) {
  return {
    input: RULES_TOKENS + count * TOKENS_PER_MODEL_IN,
    output: count * TOKENS_PER_MODEL_OUT,
  };
}

// Industry hints — mirror of _ORG_TYPE_HINTS in the server's reclassifier.py.
const ORG_TYPE_HINTS = {
  manufacturing:
    'As a Manufacturing company, expect industrial assets like: motors, gearboxes, ' +
    'conveyor belts, sorting machines, dryers, boilers, compressors, panel boards, ' +
    'MCBs, contactors, relays, timers, generators, industrial fans, pumps, ' +
    'weighing machines, and factory vehicles (tractors, trucks, forklifts).',
  healthcare:
    'As a Healthcare organisation, expect medical assets like: patient monitors, ' +
    'ventilators, defibrillators, X-ray machines, ultrasound machines, hospital beds, ' +
    'wheelchairs, oxygen concentrators, autoclaves, lab equipment, ambulances, ' +
    'plus general assets like computers, furniture, generators, and AC units.',
  aviation:
    'As an Aviation company, expect assets like: ground support equipment, baggage ' +
    'conveyors, tugs, generators, air conditioning units, fire tenders, fuel bowsers, ' +
    'vehicles, communication equipment, and office/IT equipment.',
  logistics:
    'As a Logistics company, expect assets like: trucks, trailers, forklifts, ' +
    'pallet jacks, conveyor systems, weighing scales, GPS trackers, generators, ' +
    'warehouse equipment, and office/IT equipment.',
  hospitality:
    'As a Hospitality company, expect assets like: kitchen equipment, refrigerators, ' +
    'ovens, dishwashers, laundry machines, HVAC systems, furniture, TVs, ' +
    'generators, water purifiers, and vehicles.',
  education:
    'As an Educational institution, expect assets like: projectors, computers, ' +
    'printers, laboratory equipment, furniture (desks, chairs, boards), ' +
    'HVAC systems, generators, and vehicles.',
};

function getOrgContext(orgType) {
  if (!orgType) return '';
  const key = String(orgType).toLowerCase().trim();
  for (const [hintKey, hintText] of Object.entries(ORG_TYPE_HINTS)) {
    if (key.includes(hintKey) || hintKey.includes(key)) return hintText;
  }
  return `This is a ${orgType} organisation. Classify assets appropriately for this industry.`;
}

// CSV header the app's Upload CSV expects. Order matters for readability only —
// the importer maps by header name — but keeping it fixed keeps the reply
// diff-able against a Download CSV export.
export const PROMPT_CSV_HEADER =
  'Model ID,Current Model Name,New Category,New Type,New SubType,SubType Code,New Make,New Model No';

function csvCell(value) {
  const s = value === undefined || value === null ? '' : String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * One input row per model, given to the LLM as CSV so that the reply format is
 * an obvious mirror of the input.
 */
function modelInputRow(m) {
  return [
    csvCell(m.model_id),
    csvCell(m.current_model_name),
    csvCell(m.current_category),
    csvCell(m.current_type),
    csvCell(m.current_subtype),
    csvCell(m.current_subtype_code),
    csvCell(m.current_make),
    csvCell(m.current_model_number || m.current_model_no),
    csvCell(m.asset_count || 0),
  ].join(',');
}

const INPUT_HEADER =
  'Model ID,Current Model Name,Current Category,Current Type,Current SubType,' +
  'Current SubType Code,Current Make,Current Model No,Asset Count';

/**
 * One input object per model for the JSON route. Emitted one model per line so
 * the block stays readable and the reply is an obvious mirror of the input.
 */
function modelInputJson(m) {
  return JSON.stringify({
    model_id: m.model_id || '',
    current_model_name: m.current_model_name || '',
    current_category: m.current_category || '',
    current_type: m.current_type || '',
    current_subtype: m.current_subtype || '',
    current_make: m.current_make || '',
    current_model_no: m.current_model_number || m.current_model_no || '',
    asset_count: m.asset_count || 0,
  });
}

const RULES = `=== TAXONOMY RULES ===
New Category MUST be one of these values, spelled exactly:
  Electrical Equipment, Mechanical Equipment, Industrial Machinery, Power Equipment,
  Vehicles, Computer & Peripherals, Office Equipment, Furniture & Fixture,
  Plumbing & Pumps, Safety Equipment, IOT Devices, Fuel Storage, Measuring Instruments

New Type = the asset type within that category (e.g. Motor, Gearbox, Diesel Generator,
Tractor, Fan, Pump, MCB, Contactor, Relay, Timer, Battery, Conveyor, Panel Board).
Be CONSISTENT: assets that are the same kind of thing must get the SAME Type across
the whole file. Never use "Generator" for one row and "DG Set" for another.

=== CLASSIFICATION RULES ===
Each row shows its CURRENT classification — treat it as strong context.
If the current Type / SubType is already reasonable, KEEP IT.
Only change a value when it is clearly WRONG or too generic (e.g. "Asset > Asset",
or a Make of "Make").

Decompose the model name into separate fields:
- New Category : REQUIRED, never empty, from the list above.
- New Type     : REQUIRED, never empty.
- New SubType  : REQUIRED, never empty. A generic description of the variant.
- New Make     : REQUIRED, never empty. The real brand / manufacturer.
                 Use "Generic" only when NO brand is identifiable in the name.
                 The literal word "Make" is a PLACEHOLDER, not a brand — replace it
                 with "Generic".
- New Model No : OPTIONAL. The alphanumeric product/part code ONLY, never specs.
                 Use an empty value when the name contains no model/part number.

=== ABSOLUTE RULES FOR New SubType ===
New SubType must NEVER contain:
- any brand or manufacturer name (Siemens, ABB, Tata, Cummins, Power Zone, ...)
- any model or serial number (CJ180D5P, P125EE694, CCC 0806W309169, ...)

New SubType SHOULD contain:
- physical specs: HP, RPM, KVA, tonnage, size (e.g. "25 HP 1465 RPM")
- the function or purpose (e.g. "Rotorvane", "CTC 1st Cut", "ID Fan")
- the asset type word (e.g. "Motor", "Gearbox", "Generator", "Tractor")

=== WRONG vs RIGHT ===
"SIEMENS 25 HP 1465 RPM 1st Cut Motor"
  WRONG SubType: "SIEMENS 25 HP 1465 RPM 1st Cut Motor"   (contains the brand)
  RIGHT: SubType "25 HP 1465 RPM CTC 1st Cut Motor", Make "Siemens", Model No ""

"Cummins CJ180D5P 180 kva Diesel Generator"
  RIGHT: SubType "180 KVA Diesel Generator", Make "Cummins", Model No "CJ180D5P"

"POWER ZONE CCC 0806W309169 Battery"
  RIGHT: SubType "Lead-acid Battery", Make "Power Zone", Model No "CCC 0806W309169"

"New Holland 5500 Tractor"
  RIGHT: SubType "Tractor", Make "New Holland", Model No "5500"

=== DO NOT GUESS ===
Do not invent a brand, a model number, or a specification that is not present in the
model name or its current values. When a field cannot be determined, repeat the
current value for that row rather than making something up ("Generic" is the only
allowed placeholder, and only for Make).`;

function outputSpecJson(rowCount) {
  return `=== OUTPUT FORMAT (read carefully) ===
Reply with NOTHING BUT a single JSON object. No preamble, no explanation, no
markdown code fence, no trailing commentary.

Shape:
{
  "classifications": [
    {
      "model_id": "<copied exactly from the input>",
      "current_model_name": "<copied exactly from the input>",
      "new_category": "...",
      "new_type": "...",
      "new_subtype": "...",
      "new_make": "...",
      "new_model_no": ""
    }
  ]
}

- "classifications" must contain exactly ${rowCount} object${rowCount === 1 ? '' : 's'} — one per input model, in the same order.
- Copy "model_id" and "current_model_name" EXACTLY as given. They are the keys used
  to match each result back to a model; a changed id or name will be reported as
  unmatched and NOT applied.
- All of new_category, new_type, new_subtype and new_make must be non-empty strings.
- "new_model_no" may be an empty string, but the key must still be present.
- Do NOT emit a sub-type code. The application derives it (and keeps the existing
  AssetPro code when the sub-type did not change).
- Emit strictly valid JSON: double quotes only, no trailing commas, no comments,
  no unescaped newlines inside string values.`;
}

function outputSpecCsv(rowCount) {
  return `=== OUTPUT FORMAT (read carefully) ===
Reply with NOTHING BUT CSV. No preamble, no explanation, no markdown code fence.

First line must be exactly this header:
${PROMPT_CSV_HEADER}

Then exactly ${rowCount} data row${rowCount === 1 ? '' : 's'} — one per input row, in the same order.

- Copy "Model ID" and "Current Model Name" EXACTLY as given. They are the keys used
  to match the row back; a changed id or name will be reported as unmatched and
  silently dropped from the import.
- Leave "SubType Code" EMPTY for every row. The application derives it (and keeps the
  existing AssetPro code when the sub-type did not change). Do not invent codes.
- Wrap any value that contains a comma or a quote in double quotes, doubling any
  internal quote ("" inside a quoted value).
- Do not add, remove, reorder, merge or split rows.`;
}

/**
 * Build the pasteable prompt(s).
 *
 * Splits into parts of PROMPT_BATCH_SIZE models so each paste stays inside a
 * normal chat message. Every part is self-contained: full rules + its own
 * output spec, so parts can be pasted into separate chats if needed.
 *
 * @param {Array}  models   model rows as held in AssetReclassification state
 * @param {Object} options  { orgName, orgType, format: 'json' | 'csv' }
 * @returns {Array<{ part, totalParts, from, to, count, format, text }>}
 */
export function buildReclassificationPrompts(models, { orgName = '', orgType = '', format = 'json' } = {}) {
  const rows = Array.isArray(models) ? models : [];
  if (rows.length === 0) return [];
  const isJson = format !== 'csv';

  const org = orgName || 'this organisation';
  const type = orgType || 'General';
  const orgContext = getOrgContext(orgType);
  const totalParts = Math.ceil(rows.length / PROMPT_BATCH_SIZE);

  const parts = [];
  for (let start = 0; start < rows.length; start += PROMPT_BATCH_SIZE) {
    const slice = rows.slice(start, start + PROMPT_BATCH_SIZE);
    const partNo = parts.length + 1;

    const partLine = totalParts > 1
      ? `\nThis is PART ${partNo} of ${totalParts} (models ${start + 1}-${start + slice.length} of ${rows.length}). ` +
        `Classify ONLY the ${slice.length} rows in this part, but keep your Category and Type ` +
        `wording consistent with the other parts.\n`
      : '\n';

    const text = [
      `You are an asset classification expert. You are classifying asset models for "${org}", a ${type} organisation.`,
      orgContext ? orgContext : '',
      partLine,
      `Below are ${slice.length} asset model${slice.length === 1 ? '' : 's'} with their current classification.`,
      `Re-classify each one and return the result as ${isJson ? 'JSON' : 'CSV'}.`,
      '',
      RULES,
      '',
      `=== ASSET MODELS TO CLASSIFY (${slice.length} ${isJson ? 'models' : 'rows'}) ===`,
      ...(isJson
        ? ['[', slice.map(modelInputJson).join(',\n'), ']']
        : [INPUT_HEADER, ...slice.map(modelInputRow)]),
      '',
      isJson ? outputSpecJson(slice.length) : outputSpecCsv(slice.length),
    ].filter(l => l !== null).join('\n');

    parts.push({
      part: partNo,
      totalParts,
      from: start + 1,
      to: start + slice.length,
      count: slice.length,
      format: isJson ? 'json' : 'csv',
      estimate: estimatePromptTokens(slice.length),
      text,
    });
  }

  return parts;
}

// ── Parsing the AI's JSON reply ────────────────────────────────────────────────

const attemptParse = (s) => {
  try {
    return JSON.parse(s);
  } catch (e) {
    return undefined;
  }
};

/**
 * Pull EVERY top-level JSON value out of pasted chat text.
 *
 * Returns an array, because a split classification produces one reply per part
 * and operators paste them together. Reading only the first payload would apply
 * part 1 and silently drop the rest — the exact silent-data-loss failure this
 * module is meant to avoid. All payloads found are returned; the caller
 * concatenates their entries.
 *
 * Chat UIs routinely wrap replies in a ``` fence or add a sentence before the
 * payload. Unwrapping that is a FORMAT concern only — no value is altered,
 * corrected, or guessed. If nothing parses, the caller reports a hard error
 * rather than importing a partial result.
 */
function extractJsonPayloads(text) {
  const raw = String(text || '').trim();
  if (!raw) return [];

  // Whole paste is one JSON value — the common single-part case.
  const direct = attemptParse(raw);
  if (direct !== undefined) return [direct];

  const payloads = [];

  // Every ```json ... ``` fence, in order.
  const fenceRe = /```(?:json)?\s*([\s\S]*?)\s*```/g;
  let fence;
  while ((fence = fenceRe.exec(raw)) !== null) {
    const inner = attemptParse(fence[1].trim());
    if (inner !== undefined) payloads.push(inner);
  }
  if (payloads.length > 0) return payloads;

  // Unfenced: walk the text and take each balanced { } or [ ] block in turn,
  // so several replies concatenated with prose between them are all found.
  let i = 0;
  while (i < raw.length) {
    const ch = raw[i];
    const close = ch === '{' ? '}' : ch === '[' ? ']' : null;
    if (!close) { i++; continue; }

    let depth = 0;
    let inString = false;
    let escaped = false;
    let end = -1;
    for (let j = i; j < raw.length; j++) {
      const c = raw[j];
      if (escaped) { escaped = false; continue; }
      if (c === '\\') { escaped = true; continue; }
      if (c === '"') { inString = !inString; continue; }
      if (inString) continue;
      if (c === ch) depth++;
      else if (c === close) {
        depth--;
        if (depth === 0) { end = j; break; }
      }
    }

    if (end < 0) break; // unbalanced from here on — nothing more to find
    const candidate = attemptParse(raw.slice(i, end + 1));
    if (candidate !== undefined) {
      payloads.push(candidate);
      i = end + 1;
    } else {
      i++;
    }
  }

  return payloads;
}

const str = (v) => (v === undefined || v === null ? '' : String(v).trim());

/**
 * Parse the AI's JSON reply into normalised entries for the merge step.
 *
 * Accepted shapes (all mean the same thing — this is format tolerance, not data
 * inference):
 *   { "classifications": [ ... ] }   ← what the prompt asks for
 *   { "models": [ ... ] } / { "results": [ ... ] }
 *   [ ... ]                          ← a bare array
 *
 * Per-entry keys are read in both snake_case ("new_sub_type") and camelCase
 * ("subType") spellings because chat models drift between them.
 *
 * An entry is INVALID when it carries no usable identifier, or when it proposes
 * nothing at all. Invalid entries are counted and reported — never patched up
 * with defaults and never silently dropped.
 *
 * @returns {{ entries: Array, invalid: number, error: string|null }}
 */
export function parseReclassificationJson(text) {
  const payloads = extractJsonPayloads(text);
  if (payloads.length === 0) {
    return { entries: [], invalid: 0, payloads: 0, error: 'Could not read any JSON in the pasted text. Paste the AI\'s reply exactly as it was given.' };
  }

  // Concatenate the entries from every payload found, so several parts pasted
  // together are all applied. Nothing is dropped without being counted.
  const list = [];
  let payloadsWithoutList = 0;
  for (const parsed of payloads) {
    if (Array.isArray(parsed)) {
      list.push(...parsed);
    } else if (parsed && typeof parsed === 'object') {
      const inner = parsed.classifications || parsed.models || parsed.results || null;
      if (Array.isArray(inner)) list.push(...inner);
      else payloadsWithoutList++;
    } else {
      payloadsWithoutList++;
    }
  }

  if (list.length === 0) {
    return {
      entries: [], invalid: 0, payloads: payloads.length,
      error: payloads.length === 1
        ? 'The JSON does not contain a "classifications" array.'
        : `None of the ${payloads.length} JSON blocks found contained a "classifications" array.`,
    };
  }

  const entries = [];
  let invalid = payloadsWithoutList;

  for (const item of list) {
    if (!item || typeof item !== 'object') { invalid++; continue; }

    const modelId = str(item.model_id ?? item.modelId ?? item._id ?? item.id);
    const modelName = str(item.current_model_name ?? item.currentModelName ?? item.model_name ?? item.name);
    if (!modelId && !modelName) { invalid++; continue; }

    const entry = {
      model_id: modelId,
      current_model_name: modelName,
      new_category: str(item.new_category ?? item.newCategory ?? item.category ?? item.new_classification),
      new_type: str(item.new_type ?? item.newType ?? item.type),
      new_subtype: str(item.new_subtype ?? item.new_sub_type ?? item.newSubType ?? item.subType ?? item.subtype),
      new_make: str(item.new_make ?? item.newMake ?? item.make),
    };

    // model_no is the only field allowed to be blank on purpose, so it counts as
    // "provided" whenever the key exists — that is how a value gets cleared.
    const modelNoKey = ['new_model_no', 'newModelNo', 'model_no', 'modelNo'].find(k => k in item);
    if (modelNoKey) entry.new_model_no = str(item[modelNoKey]);

    const proposesSomething =
      entry.new_category || entry.new_type || entry.new_subtype || entry.new_make || modelNoKey;
    if (!proposesSomething) { invalid++; continue; }

    entries.push(entry);
  }

  if (entries.length === 0) {
    return { entries: [], invalid, payloads: payloads.length, error: `None of the ${list.length} entries had a usable model id/name plus a proposed value.` };
  }

  return { entries, invalid, payloads: payloads.length, error: null };
}
